import type { TeacherDashboardPerformance, TeacherDashboardPerformanceRun, TeacherDashboardSummary } from "../../api/teacherWeb";
import type { StandardnessDimension } from "../result/standardness";
import { actionKey, beijingDate } from "./dashboardModel";
import { chronological, dimensions, performanceKey, summarizePerformance } from "./performanceModel";

export type TrendMetric = "score" | "tasks" | StandardnessDimension;
export type Grain = "auto" | "day" | "week" | "month";
export const trendMetrics: Array<{ key: TrendMetric; label: string }> = [{ key: "score", label: "平均分" }, ...dimensions, { key: "tasks", label: "分析任务" }];
const DAY = 86400000;
export const beijingDay = (date: number) => new Date(date + 8 * 3600000).toISOString().slice(0, 10);
const time = (date: string) => beijingDate(date.length === 10 ? `${date}T00:00:00` : date).getTime();
const mean = (values: number[]) => values.length ? values.reduce((a, b) => a + b, 0) / values.length : null;
export function metricValue(run: TeacherDashboardPerformanceRun, metric: TrendMetric) {
  return metric === "score" ? run.score?.value ?? null : run.score?.dimensions.find(axis => axis.dimension === metric)?.value ?? null;
}
function bucketStart(date: number, grain: Exclude<Grain, "auto">) {
  const shifted = new Date(date + 8 * 3600000);
  shifted.setUTCHours(0, 0, 0, 0);
  if (grain === "week") shifted.setUTCDate(shifted.getUTCDate() - (shifted.getUTCDay() + 6) % 7);
  if (grain === "month") shifted.setUTCDate(1);
  return shifted.getTime() - 8 * 3600000;
}
function nextBucket(date: number, grain: Exclude<Grain, "auto">) {
  if (grain !== "month") return date + DAY * (grain === "week" ? 7 : 1);
  const shifted = new Date(date + 8 * 3600000); shifted.setUTCMonth(shifted.getUTCMonth() + 1);
  return shifted.getTime() - 8 * 3600000;
}

export function buildTrend(data: TeacherDashboardPerformance | undefined, history: TeacherDashboardSummary["activityHistory"], tech: string, days: number, grain: Grain, endAt: string) {
  const end = time(endAt);
  const matches = (value: string) => tech === "all" || actionKey(value) === tech;
  const rawRuns = (data?.runs ?? []).filter(run => matches(run.techType ?? data!.techType) && time(run.createdAt) <= end);
  const activity = (history ?? []).filter(row => matches(row.techType) && time(row.date) <= end);
  const earliest = Math.min(rawRuns.reduce((min, run) => Math.min(min, time(run.createdAt)), end), activity.reduce((min, row) => Math.min(min, time(row.date)), end));
  const start = days ? time(beijingDay(end)) - (days - 1) * DAY : time(beijingDay(earliest));
  const span = Math.floor((end - start) / DAY) + 1;
  const automatic = span <= 45 ? "day" : span <= 730 ? "week" : "month";
  // Aggregate the whole requested range, never truncate distant records to fit the chart.
  const effectiveGrain = grain === "auto" || (grain === "day" && span > 180) || (grain === "week" && span > 1260) ? automatic : grain;
  const runs = rawRuns.filter(run => time(run.createdAt) >= start);
  const groups = new Map<number, TeacherDashboardPerformanceRun[]>();
  runs.forEach(run => { const key = bucketStart(time(run.createdAt), effectiveGrain); const group = groups.get(key) ?? []; group.push(run); groups.set(key, group); });
  const counts = new Map<number, number>();
  activity.filter(row => time(row.date) >= start).forEach(row => { const key = bucketStart(time(row.date), effectiveGrain); counts.set(key, (counts.get(key) ?? 0) + row.count); });
  const points = [];
  for (let cursor = bucketStart(start, effectiveGrain); cursor <= end; cursor = nextBucket(cursor, effectiveGrain)) {
    const next = nextBucket(cursor, effectiveGrain);
    const model = summarizePerformance(groups.get(cursor) ?? []);
    const values = Object.fromEntries(model.axes.map(axis => [axis.key, axis.value])) as Record<TrendMetric, number | null>;
    values.score = model.mean; values.tasks = counts.get(cursor) ?? 0;
    points.push({ key: beijingDay(cursor), start: beijingDay(Math.max(start, cursor)), end: beijingDay(Math.min(end, next - 1)), values, count: model.count, partial: cursor < start || next - 1 > end });
  }
  return { points, runs, grain: effectiveGrain, start, end, span };
}

// Matched first/latest attempts: the same student/action appears on both sides.
// A missing first/latest score stays missing; do not fish for a better intermediate attempt.
export function matchedProgress(runs: TeacherDashboardPerformanceRun[], metric: TrendMetric) {
  const groups = new Map<string, TeacherDashboardPerformanceRun[]>();
  chronological(runs).forEach(run => { const key = performanceKey(run); const group = groups.get(key) ?? []; group.push(run); groups.set(key, group); });
  const first: number[] = [], latest: number[] = [];
  groups.forEach(group => {
    if (group.length < 2) return;
    const a = metricValue(group[group.length - 1], metric), b = metricValue(group[0], metric);
    if (a != null && b != null) { first.push(a); latest.push(b); }
  });
  return { count: first.length, first: mean(first), latest: mean(latest), change: first.length ? mean(latest.map((value, i) => value - first[i])) : null };
}
